import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, Variants, Transition } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Gasto } from '../../services/mockApi';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateFormatter';
import { supabase } from '../../services/supabaseClient';
import { getSignedUrlFromBackend } from '../../services/backendApi';
import {
  X,
  CheckCircle,
  FileText,
  Calendar,
  DollarSign,
  Tag,
  User,
  ExternalLink,
  Loader2,
  AlertCircle,
  Image as ImageIcon
} from 'lucide-react';

// Extended Gasto interface for display purposes
interface GastoDisplay extends Gasto {
  usuario?: string;
  comprobanteUrl?: string;
}

interface GastoDetailModalProps {
  gasto: GastoDisplay | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove?: (gasto: Gasto) => void;
}

const GastoDetailModal: React.FC<GastoDetailModalProps> = ({
  gasto,
  isOpen,
  onClose,
  onApprove
}) => {
  const { isContadora, hasPermission } = useAuth();
  const [showComprobante, setShowComprobante] = useState(false);
  const [loadingComprobante, setLoadingComprobante] = useState(false);
  const [comprobanteError, setComprobanteError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [comprobanteUrlFinal, setComprobanteUrlFinal] = useState<string | null>(null);
  const [bucketEncontrado, setBucketEncontrado] = useState<string | null>(null); // Guardar bucket encontrado para renovación
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null); // Para renovación automática

  // Función auxiliar para extraer la URL del campo documento
  // Maneja tanto strings como objetos (que pueden venir de Supabase Storage)
  const extraerUrlDocumento = (valor: any): string | null => {
    if (!valor) return null;
    
    // Si es una cadena, retornarla directamente
    if (typeof valor === 'string') {
      return valor.trim() || null;
    }
    
    // Si es un objeto, intentar extraer la URL de propiedades comunes
    if (typeof valor === 'object') {
      // Intentar propiedades comunes de objetos de Supabase Storage
      if (valor.path) return valor.path;
      if (valor.url) return valor.url;
      if (valor.publicUrl) return valor.publicUrl;
      if (valor.signedUrl) return valor.signedUrl;
      if (valor.fullPath) return valor.fullPath;
      
      // Si tiene una propiedad que sea una cadena, intentar usarla
      for (const key in valor) {
        if (typeof valor[key] === 'string' && valor[key].trim()) {
          return valor[key].trim();
        }
      }
    }
    
    return null;
  };

  // Map Gasto fields to display fields
  const usuario = gasto?.usuario || gasto?.trabajador || 'Sin asignar';
  const comprobanteUrlOriginal = extraerUrlDocumento(gasto?.comprobanteUrl || gasto?.documento);
  
  // Log para debugging - más detallado
  console.log('🔍 [GastoDetailModal] Valor del campo documento:', {
    comprobanteUrl: gasto?.comprobanteUrl,
    documento: gasto?.documento,
    documentoRaw: JSON.stringify(gasto?.documento),
    comprobanteUrlOriginal,
    tipoOriginal: typeof (gasto?.comprobanteUrl || gasto?.documento),
    tipoExtraido: typeof comprobanteUrlOriginal,
    esVacio: !comprobanteUrlOriginal,
    gastoId: gasto?.id
  });

  // Obtener URL del comprobante - PRIORIZAR URLs firmadas para buckets privados
  useEffect(() => {
    const obtenerUrlComprobante = async () => {
      if (!comprobanteUrlOriginal || comprobanteUrlOriginal.trim() === '') {
        console.log('ℹ️ [GastoDetailModal] No hay URL de comprobante disponible');
        setComprobanteUrlFinal(null);
        setBucketEncontrado(null);
        return;
      }

      // Validar que la URL no esté vacía o sea solo espacios
      const urlLimpia = comprobanteUrlOriginal.trim();
      if (!urlLimpia || urlLimpia === '') {
        console.log('ℹ️ [GastoDetailModal] URL de comprobante está vacía');
        setComprobanteUrlFinal(null);
        setBucketEncontrado(null);
        return;
      }

      console.log('🔍 [GastoDetailModal] URL original del comprobante:', urlLimpia);
      
      // Si ya es una URL completa (http/https), usarla directamente
      if (urlLimpia.startsWith('http://') || urlLimpia.startsWith('https://')) {
        console.log('✅ [GastoDetailModal] URL completa detectada, usando directamente');
        setComprobanteUrlFinal(urlLimpia);
        setBucketEncontrado(null);
        return;
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        console.warn('⚠️ [GastoDetailModal] REACT_APP_SUPABASE_URL no configurada');
        setComprobanteUrlFinal(null);
        setBucketEncontrado(null);
        return;
      }

      // Lista de posibles nombres de buckets a probar (ordenados por probabilidad)
      const posiblesBuckets = ['comprobantes', 'gastos', 'documentos', 'boletas', 'receipts'];
      let urlEncontrada = false;
      
      // ✅ ESTRATEGIA: Intentar primero con frontend (timeout corto), luego backend como fallback
      // Intentar obtener URL firmada desde frontend primero (más rápido si funciona)
      for (const bucketName of posiblesBuckets) {
        try {
          console.log(`🔍 [GastoDetailModal] Intentando obtener URL firmada desde FRONTEND (bucket: ${bucketName}, archivo: ${urlLimpia})`);
          
          // ✅ Timeout corto (5 segundos) para detectar problemas rápidamente
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout al obtener URL firmada (5s)')), 5000)
          );
          
          // Usar 4 horas de expiración (14400 segundos) - balance entre seguridad y duración
          const signedUrlPromise = supabase.storage
            .from(bucketName)
            .createSignedUrl(urlLimpia, 14400);
          
          const { data, error } = await Promise.race([signedUrlPromise, timeoutPromise]) as any;
          
          // ✅ DEBUG: Log detallado del resultado
          console.log(`🔍 [GastoDetailModal] Resultado FRONTEND para bucket ${bucketName}:`, {
            tieneError: !!error,
            errorMessage: error?.message,
            errorStatus: error?.statusCode,
            tieneData: !!data,
            tieneSignedUrl: !!data?.signedUrl,
            urlLimpia
          });
          
          if (!error && data?.signedUrl) {
            console.log(`✅ [GastoDetailModal] URL firmada obtenida exitosamente desde FRONTEND (bucket: ${bucketName})`);
            console.log(`   URL (primeros 100 chars): ${data.signedUrl.substring(0, 100)}...`);
            setComprobanteUrlFinal(data.signedUrl);
            setBucketEncontrado(bucketName);
            urlEncontrada = true;
            return;
          } else if (error) {
            // Si es timeout, intentar con backend
            if (error.message?.includes('Timeout')) {
              console.log(`⏱️ [GastoDetailModal] Timeout en frontend para bucket ${bucketName}, intentando con BACKEND...`);
              break; // Salir del loop para intentar con backend
            }
            // Si es error 400/404, el archivo no existe en este bucket
            if (error.message?.includes('400') || error.message?.includes('404') || error.message?.includes('not found')) {
              console.log(`⚠️ [GastoDetailModal] Archivo no encontrado en bucket ${bucketName}:`, error.message);
              continue;
            }
            console.warn(`⚠️ [GastoDetailModal] Error al obtener URL firmada del bucket ${bucketName}:`, {
              message: error.message,
              statusCode: error.statusCode,
              error: error.error
            });
          } else {
            console.warn(`⚠️ [GastoDetailModal] No se obtuvo URL firmada (sin error pero sin signedUrl) del bucket ${bucketName}`);
          }
        } catch (error: any) {
          // Si es timeout, intentar con backend
          if (error?.message?.includes('Timeout')) {
            console.log(`⏱️ [GastoDetailModal] Timeout en frontend, intentando con BACKEND...`);
            break; // Salir del loop para intentar con backend
          }
          console.warn(`⚠️ [GastoDetailModal] Excepción al obtener URL firmada del bucket ${bucketName}:`, {
            message: error?.message,
            error: error
          });
          continue;
        }
      }

      // ✅ FALLBACK: Si falló el frontend (timeout), intentar con backend
      if (!urlEncontrada) {
        console.log('🔄 [GastoDetailModal] Intentando obtener URL firmada desde BACKEND...');
        for (const bucketName of posiblesBuckets) {
          try {
            console.log(`🔍 [GastoDetailModal] Intentando BACKEND (bucket: ${bucketName}, archivo: ${urlLimpia})`);
            
            const backendResponse = await getSignedUrlFromBackend(bucketName, urlLimpia, 14400);
            
            if (backendResponse.success && backendResponse.data?.signedUrl) {
              console.log(`✅ [GastoDetailModal] URL firmada obtenida exitosamente desde BACKEND (bucket: ${bucketName})`);
              console.log(`   URL (primeros 100 chars): ${backendResponse.data.signedUrl.substring(0, 100)}...`);
              setComprobanteUrlFinal(backendResponse.data.signedUrl);
              setBucketEncontrado(bucketName);
              urlEncontrada = true;
              return;
            } else {
              console.warn(`⚠️ [GastoDetailModal] Backend no retornó URL firmada para bucket ${bucketName}`);
            }
          } catch (error: any) {
            console.warn(`⚠️ [GastoDetailModal] Error al obtener URL firmada desde BACKEND (bucket: ${bucketName}):`, {
              message: error?.message,
              error: error
            });
            // Si es 404, el archivo no existe en este bucket
            if (error?.message?.includes('404') || error?.message?.includes('no encontrado')) {
              console.log(`⚠️ [GastoDetailModal] Archivo no encontrado en bucket ${bucketName} (backend)`);
              continue;
            }
            continue;
          }
        }
      }

      // Si no se encontró con URL firmada, intentar URL pública como fallback
      if (!urlEncontrada) {
        console.log('⚠️ [GastoDetailModal] No se encontró con URL firmada, intentando URL pública...');
        for (const bucketName of posiblesBuckets) {
          try {
            const { data } = supabase.storage
              .from(bucketName)
              .getPublicUrl(urlLimpia);
            
            if (data?.publicUrl) {
              console.log(`⚠️ [GastoDetailModal] Usando URL pública como fallback desde bucket ${bucketName}`);
              setComprobanteUrlFinal(data.publicUrl);
              setBucketEncontrado(bucketName);
              urlEncontrada = true;
              return;
            }
          } catch (error) {
            continue;
          }
        }
      }

      if (!urlEncontrada) {
        console.log('⚠️ [GastoDetailModal] Archivo no encontrado en ningún bucket.');
        console.log(`   Archivo buscado: ${urlLimpia}`);
        console.log(`   Buckets verificados: ${posiblesBuckets.join(', ')}`);
        setComprobanteUrlFinal(null);
        setBucketEncontrado(null);
      } else {
        console.log('✅ [GastoDetailModal] URL encontrada y establecida correctamente');
        console.log(`   Bucket: ${bucketEncontrado}`);
        console.log(`   URL establecida: ${comprobanteUrlFinal ? 'Sí' : 'No'}`);
      }
    };

    obtenerUrlComprobante();
  }, [comprobanteUrlOriginal]);

  // ✅ Renovar automáticamente URLs firmadas cada 3 horas (antes de expirar a las 4 horas)
  useEffect(() => {
    // Limpiar intervalo anterior si existe
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Solo renovar si tenemos bucket y archivo, y el modal está abierto
    if (!bucketEncontrado || !comprobanteUrlOriginal || !isOpen) {
      return;
    }

    const urlLimpia = comprobanteUrlOriginal.trim();
    // No renovar URLs externas
    if (urlLimpia.startsWith('http://') || urlLimpia.startsWith('https://')) {
      return;
    }

    const renovarUrl = async () => {
      if (!bucketEncontrado || !urlLimpia) {
        return;
      }

      try {
        const { data, error } = await supabase.storage
          .from(bucketEncontrado)
          .createSignedUrl(urlLimpia, 14400); // 4 horas
        
        if (!error && data?.signedUrl) {
          console.log(`🔄 [GastoDetailModal] URL firmada renovada automáticamente desde bucket ${bucketEncontrado}`);
          setComprobanteUrlFinal(data.signedUrl);
        } else {
          console.warn(`⚠️ [GastoDetailModal] Error al renovar URL firmada:`, error);
        }
      } catch (err) {
        console.warn(`⚠️ [GastoDetailModal] Excepción al renovar URL firmada:`, err);
      }
    };

    // Renovar cada 3 horas (10800000 ms) - antes de que expire a las 4 horas
    const intervaloRenovacion = 3 * 60 * 60 * 1000;
    
    refreshIntervalRef.current = setInterval(renovarUrl, intervaloRenovacion);
    
    // Renovar también cuando el modal se abre
    renovarUrl();

    // Limpiar al desmontar o cuando cambian las dependencias
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [bucketEncontrado, comprobanteUrlOriginal, isOpen]);

  // Check if user can approve (Contadora or Admin with permission)
  const canApprove = isContadora || hasPermission('approve_expenses');

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format date using utility functions
  const formatDate = formatDateDDMMYYYY;
  const formatDateWithTime = formatDateTimeDDMMYYYY;

  // Handle viewing comprobante
  const handleViewComprobante = () => {
    if (!comprobanteUrlFinal) {
      setComprobanteError('No hay comprobante disponible');
      return;
    }

    setLoadingComprobante(true);
    setComprobanteError(null);
    setShowComprobante(true);

    // Simulate loading for external resources
    setTimeout(() => {
      setLoadingComprobante(false);
    }, 500);
  };

  // Handle approve action
  const handleApprove = async () => {
    if (!gasto || !onApprove) return;

    setApproving(true);
    try {
      await onApprove(gasto);
      // Close modal after approval (optional)
      // onClose();
    } catch (error) {
      console.error('Error approving gasto:', error);
    } finally {
      setApproving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setShowComprobante(false);
    setComprobanteError(null);
    onClose();
  };

  // Animation variants
  const backdropVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const springTransition: Transition = {
    type: 'spring' as const,
    damping: 25,
    stiffness: 300
  };

  const exitTransition: Transition = {
    duration: 0.2
  };

  const modalVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: springTransition
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: exitTransition
    }
  };

  // Check if file is PDF
  const isPDF = (url: string): boolean => {
    return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('pdf');
  };

  // Check if file is image
  const isImage = (url: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    return imageExtensions.some(ext => url.toLowerCase().endsWith(ext));
  };

  if (!gasto) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            style={{ zIndex: 10020 }}
            onClick={handleClose}
            aria-hidden="true"
          >
            {/* Modal */}
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
              style={{ zIndex: 10021 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 id="modal-title" className="text-2xl font-bold text-gray-900">
                      Detalle del Gasto
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">ID: {gasto.id}</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Cerrar modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {/* Main Information Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Fecha */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5 text-gray-500" />
                        <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                          Fecha
                        </label>
                      </div>
                      <p className="text-lg font-medium text-gray-900">
                        {formatDate(gasto.fecha)}
                      </p>
                    </motion.div>

                    {/* Monto */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 }}
                      className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                          Monto
                        </label>
                      </div>
                      <p className="text-2xl font-bold text-green-700">
                        {formatCurrency(gasto.monto)}
                      </p>
                    </motion.div>

                    {/* Categoría */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-blue-50 rounded-lg p-4 border border-blue-200"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-5 h-5 text-blue-600" />
                        <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                          Categoría
                        </label>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {gasto.categoria}
                      </span>
                    </motion.div>

                    {/* Usuario */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 }}
                      className="bg-purple-50 rounded-lg p-4 border border-purple-200"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-5 h-5 text-purple-600" />
                        <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                          Usuario
                        </label>
                      </div>
                      <p className="text-lg font-medium text-gray-900">
                        {usuario}
                      </p>
                    </motion.div>
                  </div>

                  {/* Descripción */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 block">
                      Descripción
                    </label>
                    <p className="text-gray-900 leading-relaxed">
                      {gasto.descripcion}
                    </p>
                  </motion.div>

                  {/* Estado */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="flex items-center gap-3"
                  >
                    <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Estado:
                    </label>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                        gasto.estado_alerta === 'aprobado'
                          ? 'bg-green-100 text-green-800 border-green-200'
                          : gasto.estado_alerta === 'rechazado'
                          ? 'bg-red-100 text-red-800 border-red-200'
                          : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                      }`}
                    >
                      {gasto.estado_alerta === 'aprobado' ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1.5" />
                          Aprobado
                        </>
                      ) : gasto.estado_alerta === 'rechazado' ? (
                        <>
                          <AlertCircle className="w-4 h-4 mr-1.5" />
                          Rechazado
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 mr-1.5" />
                          Pendiente
                        </>
                      )}
                    </span>
                  </motion.div>

                  {/* Comprobante Section */}
                  {comprobanteUrlFinal && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-600" />
                          <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Comprobante
                          </label>
                        </div>
                        <button
                          onClick={handleViewComprobante}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Ver Comprobante
                        </button>
                      </div>

                      {/* Comprobante Preview */}
                      {showComprobante && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 border border-gray-300 rounded-lg overflow-hidden bg-white"
                        >
                          {loadingComprobante ? (
                            <div className="p-8 text-center">
                              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                              <p className="text-sm text-gray-600">Cargando comprobante...</p>
                            </div>
                          ) : comprobanteError ? (
                            <div className="p-8 text-center">
                              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                              <p className="text-sm text-red-600">{comprobanteError}</p>
                            </div>
                          ) : isPDF(comprobanteUrlFinal) ? (
                            <div className="p-4">
                              <div className="flex items-center justify-center bg-gray-100 rounded-lg p-8">
                                <div className="text-center">
                                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm text-gray-600 mb-4">Documento PDF</p>
                                  <a
                                    href={comprobanteUrlFinal}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Abrir PDF
                                  </a>
                                </div>
                              </div>
                            </div>
                          ) : isImage(comprobanteUrlFinal) ? (
                            <div className="p-4">
                              <img
                                src={comprobanteUrlFinal}
                                alt="Comprobante del gasto"
                                className="w-full h-auto rounded-lg border border-gray-200"
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  const imgElement = e.target as HTMLImageElement;
                                  const errorUrl = imgElement.src;
                                  
                                  console.error('❌ [GastoDetailModal] Error al cargar imagen:', {
                                    url: errorUrl,
                                    error: e,
                                    target: imgElement
                                  });
                                  
                                  // Si tenemos bucket y archivo, renovar URL firmada inmediatamente
                                  if (bucketEncontrado && comprobanteUrlOriginal && 
                                      typeof comprobanteUrlOriginal === 'string' && 
                                      !comprobanteUrlOriginal.startsWith('http')) {
                                    console.log('🔄 [GastoDetailModal] URL expirada o inválida, renovando inmediatamente...');
                                    const renovarUrlInmediata = async () => {
                                      try {
                                        const { data, error } = await supabase.storage
                                          .from(bucketEncontrado)
                                          .createSignedUrl(comprobanteUrlOriginal, 14400); // 4 horas
                                        
                                        if (!error && data?.signedUrl) {
                                          console.log(`✅ [GastoDetailModal] URL firmada renovada inmediatamente desde bucket ${bucketEncontrado}`);
                                          setComprobanteUrlFinal(data.signedUrl);
                                          setComprobanteError(null);
                                        } else {
                                          console.warn(`⚠️ [GastoDetailModal] Error al renovar URL firmada:`, error);
                                          // Si falla, intentar otros buckets
                                          const posiblesBuckets = ['comprobantes', 'gastos', 'documentos', 'boletas', 'receipts'];
                                          for (const bucketName of posiblesBuckets) {
                                            if (bucketName === bucketEncontrado) continue;
                                            try {
                                              const { data: fallbackData, error: fallbackError } = await supabase.storage
                                                .from(bucketName)
                                                .createSignedUrl(comprobanteUrlOriginal, 14400);
                                              
                                              if (!fallbackError && fallbackData?.signedUrl) {
                                                console.log(`✅ [GastoDetailModal] URL firmada obtenida desde bucket alternativo ${bucketName}`);
                                                setComprobanteUrlFinal(fallbackData.signedUrl);
                                                setBucketEncontrado(bucketName);
                                                setComprobanteError(null);
                                                return;
                                              }
                                            } catch (err) {
                                              continue;
                                            }
                                          }
                                          if (showComprobante) {
                                            setComprobanteError('No se pudo cargar el comprobante. El archivo puede no existir o no estar disponible.');
                                          }
                                        }
                                      } catch (err) {
                                        console.error('❌ [GastoDetailModal] Error al intentar renovar URL firmada:', err);
                                        if (showComprobante) {
                                          setComprobanteError('No se pudo cargar el comprobante. El archivo puede no existir o no estar disponible.');
                                        }
                                      }
                                    };
                                    renovarUrlInmediata();
                                  } else {
                                    // No tenemos información para renovar - intentar obtener URL firmada desde cero
                                    if (comprobanteUrlOriginal && 
                                        typeof comprobanteUrlOriginal === 'string' && 
                                        !comprobanteUrlOriginal.startsWith('http')) {
                                      console.log('🔄 [GastoDetailModal] Intentando obtener URL firmada desde cero...');
                                      const obtenerUrlFirmada = async () => {
                                        try {
                                          const posiblesBuckets = ['comprobantes', 'gastos', 'documentos', 'boletas', 'receipts'];
                                          
                                          for (const bucketName of posiblesBuckets) {
                                            try {
                                              const { data, error } = await supabase.storage
                                                .from(bucketName)
                                                .createSignedUrl(comprobanteUrlOriginal, 14400); // 4 horas
                                              
                                              if (!error && data?.signedUrl) {
                                                console.log(`✅ [GastoDetailModal] URL firmada obtenida desde bucket ${bucketName}`);
                                                setComprobanteUrlFinal(data.signedUrl);
                                                setBucketEncontrado(bucketName);
                                                setComprobanteError(null);
                                                return;
                                              }
                                            } catch (err: any) {
                                              continue;
                                            }
                                          }
                                          if (showComprobante) {
                                            setComprobanteError('No se pudo cargar el comprobante. El archivo puede no existir o no estar disponible.');
                                          }
                                        } catch (err) {
                                          console.error('❌ [GastoDetailModal] Error al intentar obtener URL firmada:', err);
                                          if (showComprobante) {
                                            setComprobanteError('No se pudo cargar el comprobante. El archivo puede no existir o no estar disponible.');
                                          }
                                        }
                                      };
                                      obtenerUrlFirmada();
                                    } else {
                                      // Error en URL externa o sin información
                                      if (showComprobante) {
                                        setComprobanteError('No se pudo cargar el comprobante. El archivo puede no existir o no estar disponible.');
                                      }
                                    }
                                  }
                                }}
                                onLoad={() => {
                                  console.log('✅ [GastoDetailModal] Imagen cargada exitosamente');
                                  setComprobanteError(null);
                                }}
                              />
                            </div>
                          ) : (
                            <div className="p-4">
                              <div className="flex items-center justify-center bg-gray-100 rounded-lg p-8">
                                <div className="text-center">
                                  <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm text-gray-600 mb-4">Comprobante disponible</p>
                                  <a
                                    href={comprobanteUrlFinal}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Ver Archivo
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  )}

                  {/* No Comprobante Message */}
                  {!comprobanteUrlFinal && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                        <p className="text-sm text-yellow-800">
                          No hay comprobante asociado a este gasto.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Footer with Actions */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cerrar
                </button>

                {canApprove && !gasto.sincronizado && (
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="inline-flex items-center px-6 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {approving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Aprobando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aprobar Gasto
                      </>
                    )}
                  </button>
                )}

                {gasto.estado_alerta === 'aprobado' && (
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Gasto ya aprobado</span>
                  </div>
                )}
                {gasto.estado_alerta === 'rechazado' && (
                  <div className="flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Gasto rechazado</span>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GastoDetailModal;

